import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { Object3D } from 'three';

import { extend, Object3DNode } from '@react-three/fiber';
import { Instance, Instances } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';

import { useModelLoader } from '../../hooks/index.js';

extend({ Instances });

export interface ModelInstancesProps
  extends Omit<Object3DNode<Instances, typeof Instances>, 'args' | 'instances' | 'id'> {
  url: string;
  setRef?: React.Dispatch<React.SetStateAction<Record<string, Instances>>>;
  inject?: (node: Object3D) => void;
  instances: Instance[];
}

export const ModelInstances = forwardRef<Instances, ModelInstancesProps>(
  ({ url, setRef, instances, inject, ...restProps }, ref) => {
    const instancesRef = useRef<Instances>(null);
    const { scene } = useModelLoader(url);

    const internalSetRef = useEventCallback((self: Instances) => {
      setRef?.((prevState) => ({ ...prevState, [url]: self }));
    });

    useImperativeHandle(ref, () => instancesRef.current!, []);

    useLayoutEffect(() => {
      if (instancesRef.current) {
        internalSetRef(instancesRef.current);
      }
    }, [internalSetRef]);

    const injectedScene = useMemo(() => {
      if (inject) {
        scene.traverse(inject);
      }

      return scene;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scene]);

    return (
      <instances
        ref={instancesRef}
        args={[injectedScene]}
        frustumCulled={false}
        instances={instances}
        instancesAutoMount={false}
        {...restProps}
        raycast={() => null}
      />
    );
  }
);
